// src/services/permissionsService.js
import {api} from './api';
// import errorHandlingService from './error/errorHandlingService';

/**
 * Service for managing permissions constants
 */
const permissionsService = {
  /**
   * Get permissions constants (resources, actions, scopes)
   * @returns {Promise<Object>} Object containing resources, actions, and scopes
   */
  getConstants: async () => {
    try {
      const response = await api.get('/permissions');
      return response;
    } catch (error) {
      // Log and handle the error
    //   errorHandlingService.handleApiError(error, 'Failed to fetch permission constants');
      throw error;
    }
  },
  
  /**
   * Check if user has a specific permission
   * @param {Array} userPermissions - User's permissions array
   * @param {string} resource - Resource to check
   * @param {string} action - Action to check
   * @param {string} [scope='all'] - Scope to check
   * @returns {boolean} Whether user has the permission
   */
  hasPermission: (userPermissions, resource, action, scope = 'all') => {
    if (!userPermissions || !Array.isArray(userPermissions)) {
      return false;
    }
    
    return userPermissions.some(permission => 
      permission.resource === resource && 
      permission.action === action && 
      (permission.scope === scope || permission.scope === 'all')
    );
  },
  
  /**
   * Convert a flat permission list to a grouped structure for UI with scope support
   * @param {Array} permissions - Flat permissions list
   * @param {Array|Object} resources - Available resources (array or object)
   * @param {Array|Object} actions - Available actions (array or object)
   * @returns {Object} Grouped permissions by resource and action with scope info
   */
  groupPermissionsByResource: (permissions, resources, actions) => {
    const groupedPermissions = {};
    
    // Convert resources to array if it's an object
    const resourcesArray = Array.isArray(resources) 
      ? resources 
      : Object.values(resources);
    
    // Convert actions to array if it's an object  
    const actionsArray = Array.isArray(actions)
      ? actions
      : Object.values(actions);
    
    // Initialize structure
    resourcesArray.forEach(resource => {
      groupedPermissions[resource] = {};
      actionsArray.forEach(action => {
        groupedPermissions[resource][action] = {
          enabled: false,
          scope: 'all' // Default scope
        };
      });
    });
    
    // Fill in permissions
    permissions.forEach(permission => {
      if (groupedPermissions[permission.resource]) {
        groupedPermissions[permission.resource][permission.action] = {
          enabled: true,
          scope: permission.scope || 'all'
        };
      }
    });
    
    return groupedPermissions;
  },
  
  /**
   * Convert grouped permissions back to flat list (legacy method)
   * @param {Object} groupedPermissions - Permissions grouped by resource
   * @param {string} [scope='all'] - Scope to use for all permissions
   * @returns {Array} Flat list of permissions
   */
  flattenGroupedPermissions: (groupedPermissions, scope = 'all') => {
    const flatPermissions = [];
    
    Object.entries(groupedPermissions).forEach(([resource, actions]) => {
      Object.entries(actions).forEach(([action, enabled]) => {
        if (enabled) {
          flatPermissions.push({
            resource,
            action,
            scope
          });
        }
      });
    });
    
    return flatPermissions;
  },
  
  /**
   * Convert grouped permissions with scopes back to flat list
   * @param {Object} groupedPermissions - Permissions grouped by resource with scope info
   * @returns {Array} Flat list of permissions
   */
  flattenGroupedPermissionsWithScopes: (groupedPermissions) => {
    const flatPermissions = [];
    
    Object.entries(groupedPermissions).forEach(([resource, actions]) => {
      Object.entries(actions).forEach(([action, permission]) => {
        if (permission && permission.enabled) {
          flatPermissions.push({
            resource,
            action,
            scope: permission.scope || 'all'
          });
        }
      });
    });
    
    return flatPermissions;
  },
  
  /**
   * Transform permissions constants from object to array
   * @param {Object} constants - The constants object from API
   * @returns {Object} Object with resources, actions, and scopes as arrays
   */
  transformConstantsToArrays: (constants) => {
    return {
      resources: Object.values(constants.resources || {}),
      actions: Object.values(constants.actions || {}),
      scopes: Object.values(constants.scopes || {})
    };
  }
};

export default permissionsService;